import React from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  CreditCard,
  ShieldCheck,
  GraduationCap,
  Globe,
  ChevronDown,
  LogOut,
  ExternalLink,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { APP_INFO } from '../../config/constants';
import logo from '../../assets/logo.png';

const sidebarNav = [
  { label: 'Overview', path: '/admin', icon: LayoutDashboard, end: true },
  { label: 'Users', path: '/admin/users', icon: Users },
  { label: 'Donations', path: '/admin/donations', icon: CreditCard },
  { label: 'Ambassadors', path: '/admin/ambassadors', icon: ShieldCheck },
  { label: 'Alumni Requests', path: '/admin/alumni-requests', icon: GraduationCap },
];

function UserAvatar({ name, image }: { name?: string; image?: string }) {
  const initials = (name || 'A')
    .split(' ')
    .map(n => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  if (image) {
    return <img src={image} alt={name || 'Admin'} className="w-8 h-8 object-cover border border-[#30363d]" />;
  }

  return (
    <div className="w-8 h-8 bg-accent-500 flex items-center justify-center text-white text-xs font-medium shrink-0">
      {initials}
    </div>
  );
}

export default function AdminLayout({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const shortName = APP_INFO.organization.split(' ')[0];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="admin-shell min-h-screen flex flex-col bg-[#0d1117] text-[#8b949e]" style={{ maxWidth: '100vw' }}>
      <header className="h-12 shrink-0 border-b border-[#21262d] bg-[#0d1117] flex items-center px-3 sm:px-4 lg:px-6" style={{ minWidth: 0 }}>
        <div className="flex items-center gap-8 flex-1 min-w-0">
          <Link to="/" className="flex items-center gap-2 shrink-0 hover:opacity-80 transition-opacity">
            <div className="w-7 h-7 bg-white/10 border border-white/15 flex items-center justify-center p-1">
              <img src={logo} alt="Imarat Shariah" className="w-full h-full object-contain" />
            </div>
            <span className="text-white font-medium text-sm">{shortName}</span>
          </Link>
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/projects" className="text-sm text-[#8b949e] hover:text-white transition-colors">
              Projects
            </Link>
            <Link to="/dashboard" className="text-sm text-[#8b949e] hover:text-white transition-colors">
              Dashboard
            </Link>
            <Link to="/" className="text-sm text-[#8b949e] hover:text-white transition-colors inline-flex items-center gap-1">
              Site
              <ExternalLink className="w-3 h-3" />
            </Link>
            <NavLink
              to="/admin"
              className={({ isActive }) =>
                `text-sm font-medium relative pb-3 pt-3 transition-colors ${
                  isActive
                    ? 'text-white border-b-2 border-primary-500'
                    : 'text-[#8b949e] hover:text-white'
                }`
              }
            >
              Admin
            </NavLink>
          </nav>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <Link to="/" aria-label="Public site" className="p-2 text-[#8b949e] hover:text-white transition-colors hidden sm:block">
            <Globe className="w-4 h-4" />
          </Link>
          <Link
            to="/admin/donations"
            className="hidden sm:inline-flex items-center px-4 py-1.5 text-sm text-white font-normal bg-gradient-to-r from-primary-500 to-accent-500 hover:from-primary-600 hover:to-accent-600 transition-all"
          >
            Export
          </Link>
          <div className="relative group">
            <button type="button" className="flex items-center gap-2 pl-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500">
              <UserAvatar name={user?.name} image={user?.image} />
              <ChevronDown className="w-3.5 h-3.5 text-[#8b949e] hidden lg:block" />
            </button>
            <div className="absolute right-0 top-full mt-1 w-48 bg-[#161b22] border border-[#30363d] py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible group-focus-within:opacity-100 group-focus-within:visible transition-all z-50">
              <div className="px-4 py-2 border-b border-[#30363d]">
                <p className="text-sm text-white truncate">{user?.name || 'Admin'}</p>
                <p className="text-xs text-[#8b949e] truncate">{user?.userCode}</p>
              </div>
              <Link to="/profile" className="block px-4 py-2 text-sm text-[#8b949e] hover:text-white hover:bg-[#21262d]">
                Profile
              </Link>
              <Link to="/dashboard" className="block px-4 py-2 text-sm text-[#8b949e] hover:text-white hover:bg-[#21262d]">
                User Dashboard
              </Link>
              <button
                type="button"
                onClick={handleLogout}
                className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-[#21262d] flex items-center gap-2"
              >
                <LogOut className="w-3.5 h-3.5" />
                Sign out
              </button>
            </div>
          </div>
        </div>
      </header>

      <nav className="lg:hidden flex overflow-x-auto gap-1 px-3 py-2 border-b border-[#21262d] bg-[#161b22]" aria-label="Admin sections">
        {sidebarNav.map(item => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.end}
            className={({ isActive }) =>
              `shrink-0 flex items-center gap-2 px-3 py-2 text-xs whitespace-nowrap transition-colors ${
                isActive
                  ? 'bg-[#21262d] text-white border-b-2 border-primary-500'
                  : 'text-[#8b949e] hover:text-white'
              }`
            }
          >
            <item.icon className="w-3.5 h-3.5 shrink-0" />
            {item.label}
          </NavLink>
        ))}
      </nav>

      <div className="flex flex-1 min-h-0" style={{ minWidth: 0 }}>
        <aside className="hidden lg:flex w-56 shrink-0 flex-col border-r border-[#21262d] bg-[#161b22]">
          <div className="p-4 border-b border-[#21262d]">
            <p className="text-[10px] uppercase tracking-widest text-[#8b949e] mb-2">Workspace</p>
            <button
              type="button"
              className="w-full flex items-center justify-between px-3 py-2 text-sm text-white bg-[#21262d] border border-[#30363d] hover:border-[#484f58] transition-colors"
            >
              <span className="truncate">{APP_INFO.organization.toLowerCase().replace(/\s+/g, '-')}</span>
              <ChevronDown className="w-4 h-4 shrink-0 text-[#8b949e]" />
            </button>
          </div>
          <nav className="flex-1 p-3 space-y-0.5">
            {sidebarNav.map(item => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.end}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 text-sm transition-colors ${
                    isActive
                      ? 'bg-[#21262d] text-white border-l-2 border-primary-500 -ml-px pl-[11px]'
                      : 'text-[#8b949e] hover:text-white hover:bg-[#21262d]/60'
                  }`
                }
              >
                <item.icon className="w-4 h-4 shrink-0" />
                {item.label}
              </NavLink>
            ))}
          </nav>
        </aside>

        <main className="admin-theme flex-1 overflow-auto admin-main" style={{ minWidth: 0 }}>
          {children}
        </main>
      </div>
    </div>
  );
}
